import { getByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet, NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { DesignSystem } from '@adornis/chemistry/elements/theming/design.js';
import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { prettyUserRole, UserRole } from '@ils/static/enums.js';
import { html, nothing } from 'lit';
import { DateTime } from 'luxon';
import { ILSUser } from '../../db/ils-user.js';

const userSelector = {
  _id: 1,
  roles: { name: 1, contexts: 1 },
  email: 1,
  username: 1,
  ecommerce: {
    firstName: 1,
    lastName: 1,
    fullName: 1,
    street: 1,
    zip: 1,
    city: 1,
    country: 1,
    phone: 1,
    acceptedLegal: 1,
  },
  profile: {
    gender: 1,
    birthplace: 1,
    birthCountry: 1,
    targetFederalState: 1,
    b2Certificate: 1,
    dateOfBirth: 1,
    hasParticipatedExam: 1,
    participatedC1Course: 1,
    wantsNewsletter: 1,
  },
  coInvoice: 1,
  specialization: 1,
  assignedOSCETypes: 1,
} as const satisfies BaseQLSelectionSet<ILSUser>;

type NarrowedUser = NarrowedEntity<ILSUser, typeof userSelector>;

const roles = (user: NarrowedUser) => html`
  <x-text bold> Rollen: </x-text>
  <x-text>${user.roles.map(role => prettyUserRole(role.name as UserRole)).join(',')}</x-text>
`;

const inspectorSpecialization = (user: NarrowedUser) => {
  if (!user.roles.find(role => role.name === UserRole.INSPECTOR)) return nothing;
  return html`
    <x-text bold> Spezialisierung: </x-text>
    <x-text>${user.specialization}</x-text>
  `;
};

const testerOsces = (user: NarrowedUser) => {
  if (!user.roles.find(role => role.name === UserRole.TESTER)) return nothing;
  return html`
    <x-text bold> Zugewiesene OSCEs: </x-text>
    <x-text>${user.assignedOSCETypes?.join(', ') || '-'}</x-text>
  `;
};

const gender = (user: NarrowedUser) => {
  if (!user.roles.find(role => role.name === UserRole.TRAINEE)) return nothing;
  return html`
    <x-text bold> Geschlecht: </x-text>
    <x-text>${user.profile?.gender || '-'}</x-text>
  `;
};

const basicInfo = (user: NarrowedUser) => {
  return html`
    <x-text bold> Vorname: </x-text>
    <x-text>${user.ecommerce?.firstName || '-'}</x-text>
    <x-text bold> Nachname: </x-text>
    <x-text>${user.ecommerce?.lastName || '-'}</x-text>
    <x-text bold> E-Mail: </x-text>
    <x-text>${user.email || '-'}</x-text>
  `;
};

const contact = (user: NarrowedUser) => {
  return html`
    <x-text bold> Adresse: </x-text>
    <x-text>${user.ecommerce?.street || '-'}</x-text>
    <x-text bold> PLZ: </x-text>
    <x-text>${user.ecommerce?.zip || '-'}</x-text>
    <x-text bold> Stadt: </x-text>
    <x-text>${user.ecommerce?.city || '-'}</x-text>
    <x-text bold> Land: </x-text>
    <x-text>${user.ecommerce?.country || '-'}</x-text>
    <x-text bold> Telefonnummer: </x-text>
    <x-text>${user.ecommerce?.phone || '-'}</x-text>
  `;
};

const additionalTraineeInfo = (user: NarrowedUser) => {
  if (!user.roles.find(role => role.name === UserRole.TRAINEE)) return nothing;
  return html`
    <x-text bold> Geburtsdatum: </x-text>
    <x-text>${user.profile?.dateOfBirth?.toLocaleString(DateTime.DATE_SHORT) || '-'}</x-text>
    <x-text bold> Geburtsort: </x-text>
    <x-text>${user.profile?.birthplace || '-'}</x-text>
    <x-text bold> Geburtsland: </x-text>
    <x-text>${user.profile?.birthCountry || '-'}</x-text>
    <x-text bold> c/o (Rechnung): </x-text>
    <x-text>${user.coInvoice || '-'}</x-text>
    <x-text bold> Ziel-Bundesland: </x-text>
    <x-text>${user.profile?.targetFederalState || '-'}</x-text>
    <x-text bold> B2 Zertifikat: </x-text>
    <x-text>${user.profile?.b2Certificate ? 'Vorhanden' : 'Nicht vorhanden'}</x-text>
    <x-text bold> Erhält Newsletter: </x-text>
    <x-text>${user.profile?.wantsNewsletter ? 'Ja' : 'Nein'}</x-text>
  `;
};

const ILSUserView = stateful(({ userID, close, cancel }: { userID: string; close: () => void; cancel: () => void }) => {
  const user = useQuery(() => getByID(ILSUser, userID)(userSelector), [userID]);
  const design = useObservable(() => DesignSystem.currentTheme, []);

  if (user.loading) {
    return html`<x-flex crossaxis-center center padding="lg"> <ils-loader></ils-loader> </x-flex>`;
  }

  if (user.error) {
    return html`
      <x-text> Fehler beim Laden des Nutzers </x-text>
      <pre>${user.error.message}</pre>
    `;
  }

  if (!user.value) {
    return html` <x-text> Nutzer nicht gefunden </x-text> `;
  }

  return html`
    <x-grid columns="max-content max-content" space="sm" padding="md">
      <x-h2 ${css({ gridColumn: 'span 2', paddingBottom: design?.spacing.md })}>Nutzer Details</x-h2>
      ${roles(user.value)} ${inspectorSpecialization(user.value)} ${testerOsces(user.value)} ${basicInfo(user.value)}
      ${gender(user.value)} ${contact(user.value)} ${additionalTraineeInfo(user.value)}
      <x-flex horizontal end ${css({ gridColumn: 'span 2' })}>
        <x-button mode="outline" @click=${() => close()}> Schließen </x-button>
      </x-flex>
    </x-grid>
  `;
});

export { ILSUserView };
